'use client';
import { useImageFallback } from '@susu/custom-made/hooks/useImageFallback';
import { onEnter } from '@susu/custom-made/utils/utils';
import cn from 'classnames';

export enum TileVariant {
  /** Fabric filters: no container chrome, selection is a ring drawn around the thumbnail. */
  Bare = 'bare',
  /** Monogram selection: borderless until hovered, dark border when selected. */
  Bordered = 'bordered',
}

const CONTAINER_BARE = 'text-center p-0 border border-white min-w-[82px] gap-[9px]';
const CONTAINER_BORDERED =
  'gap-12 p-3 rounded-[8px] border border-solid bg-surface transition-[color,background-color,border-color]';

const THUMBNAIL_BARE = 'inline-block w-[31px] h-[31px]';
const THUMBNAIL_BORDERED = 'shrink-0 overflow-hidden rounded-md bg-background-gray-light w-48 h-48';

const LABEL_BARE = 'text-xs not-italic font-normal leading-xs';
const LABEL_BORDERED = 'text-sm leading-sm font-light text-text';

const getContainerClasses = (variant: TileVariant, isSelected: boolean) =>
  cn('flex cursor-pointer items-center box-border', {
    [CONTAINER_BARE]: variant === TileVariant.Bare,
    [CONTAINER_BORDERED]: variant === TileVariant.Bordered,
    'border-border-selected': variant === TileVariant.Bordered && isSelected,
    // Transparent rather than absent so hovering and selecting never shift the layout.
    'border-transparent hover:border-border-hover': variant === TileVariant.Bordered && !isSelected,
  });

const getThumbnailClasses = (variant: TileVariant, isSelected: boolean) => {
  if (variant === TileVariant.Bare) {
    return cn(THUMBNAIL_BARE, isSelected ? 'border border-gray-800 rounded-md p-2' : 'p-3');
  }

  return THUMBNAIL_BORDERED;
};

const getLabelClasses = (variant: TileVariant) =>
  cn(variant === TileVariant.Bare ? LABEL_BARE : LABEL_BORDERED, 'overflow-hidden text-ellipsis whitespace-nowrap');

export interface TileProps {
  tileId: number;
  tileText: string;
  /** Hex colour when `tileThumbnailType` is `color`, image URL when `image`. */
  tileThumbnail: string;
  tileThumbnailType?: 'color' | 'image';
  /** Tried before the generic fallback when an `image` thumbnail fails to load. */
  tileFallbackThumbnail?: string;
  tileVariant?: TileVariant;
  isSelected: boolean;
  testId: string;
  tileClick: (tileId: number) => void;
}

export const Tile = ({
  tileId,
  tileText,
  tileThumbnail,
  tileThumbnailType = 'image',
  tileFallbackThumbnail,
  tileVariant = TileVariant.Bare,
  isSelected,
  testId,
  tileClick,
}: TileProps) => {
  const { handleImageError, handleImageLoad } = useImageFallback();
  const isBare = tileVariant === TileVariant.Bare;
  const imageKey = String(tileId);

  return (
    <div
      className={getContainerClasses(tileVariant, isSelected)}
      data-testid={testId}
      role="button"
      tabIndex={0}
      onClick={() => tileClick(tileId)}
      onKeyDown={e => {
        onEnter(e, () => tileClick(tileId));
      }}
    >
      <div className={getThumbnailClasses(tileVariant, isSelected)}>
        {tileThumbnailType === 'color' && (
          <span className="rounded-md block w-full h-full" style={{ backgroundColor: tileThumbnail }} />
        )}
        {tileThumbnailType === 'image' && (
          <img
            className={cn({ 'w-full h-full object-cover': !isBare })}
            src={tileThumbnail}
            alt={tileText}
            width={isBare ? 25 : undefined}
            height={isBare ? 25 : undefined}
            onError={e => {
              handleImageError(e, imageKey, tileFallbackThumbnail);
            }}
            onLoad={e => {
              handleImageLoad(e, imageKey);
            }}
          />
        )}
      </div>
      <div className={getLabelClasses(tileVariant)}>{tileText}</div>
    </div>
  );
};
